import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, CircularProgress, Button, Grid, Grow } from '@mui/material';
import { CheckCircle, Error, Email } from '@mui/icons-material';
import API from 'api';
import { toast } from 'react-toastify';
import styles from './index.module.scss';
import SignInImg from 'Assets/Images/login.png';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error'
  const [message, setMessage] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [userType, setUserType] = useState('advisor');
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        const token = searchParams.get('token');

        if (!token) {
          // Redirect to dashboard if no token provided
          toast.error('Invalid verification link. Please use the link from your email.');
          navigate('/');
          return;
        }

        console.log('Verifying email with token:', token.substring(0, 20) + '...');

        const response = await API.auth.verifyEmail({ token });

        if (response.data.success) {
          setStatus('success');
          setMessage(response.data.message || 'Email verified successfully! You can now login to your account.');
          const detectedUserType = response.data.data?.userType || 'advisor';
          setUserType(detectedUserType);
          
          // Show success toast
          toast.success('Email verified successfully!');
        }

      } catch (error) {
        console.error('Verification error:', error);
        
        setStatus('error');
        const code = error.response?.data?.code || '';
        setErrorCode(code);
        
        const errorMessage = error.response?.data?.message;
        
        if (code === 'EXPIRED') {
          setMessage('Verification link has expired. Please request a new one.');
        } else if (code === 'ALREADY_USED') {
          setMessage('This link has already been used. You can now login.');
          setUserType(error.response?.data?.data?.userType || 'advisor');
        } else if (code === 'INVALID_TOKEN') {
          setMessage('Invalid verification link. Please check your email or request a new link.');
        } else {
          setMessage(errorMessage || 'Verification failed. Please try again.');
        }
      }
    };

    verifyEmail();
  }, [searchParams]);

  // Countdown and redirect effect for success
  useEffect(() => {
    if (status === 'success') {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            // Navigate to sign-in page
            navigate(`/sign-in/${userType}`);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [status, userType, navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(to right, #041e42, #78a8e6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2,
      }}
    >
      <Grid container spacing={0}>
        {/* Left Side - Info */}
        <Grow in timeout={1000}>
          <Grid item xs={12} md={5} sx={{ color: 'white', p: 4, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              EMAIL VERIFICATION
            </Typography>
            <Typography variant="h5" gutterBottom>
              Trusted Advisory Service
            </Typography>
            <Typography variant="body1" sx={{ mt: 2, lineHeight: 1.8 }}>
              We're verifying your email address to ensure the security of your account.
            </Typography>
            <Box component="img" src={SignInImg} alt="illustration" sx={{ width: '60%', maxWidth: 400, mt: 4 }} />
          </Grid>
        </Grow>

        {/* Right Side - Verification Status */}
        <Grid item xs={12} md={7}>
          <Grow in timeout={1000}>
            <Card sx={{ height: '100%', width: "80%", m: "auto", borderRadius: 4, p: 3 }}>
              <CardContent>
                
                {/* Verifying State */}
                {status === 'verifying' && (
                  <Box className={styles.centerContent}>
                    <Box className={styles.iconContainer}>
                      <CircularProgress size={80} thickness={4} />
                      <Email className={styles.emailIcon} />
                    </Box>
                    <Typography variant="h4" className={styles.title} sx={{ mt: 3, mb: 2 }}>
                      Verifying your email...
                    </Typography>
                    <Typography variant="body1" color="text.secondary" className={styles.subtitle}>
                      Please wait while we verify your email address
                    </Typography>
                    <Box className={styles.dotsContainer}>
                      <span className={styles.dot}></span>
                      <span className={styles.dot}></span>
                      <span className={styles.dot}></span>
                    </Box>
                  </Box>
                )}

                {/* Success State */}
                {status === 'success' && (
                  <Box className={styles.centerContent}>
                    <Box className={styles.successIconContainer}>
                      <CheckCircle className={styles.successIcon} />
                    </Box>
                    <Typography variant="h4" className={styles.successTitle} sx={{ mt: 2, mb: 2 }}>
                      Email Verified Successfully!
                    </Typography>
                    <Typography variant="body1" color="text.secondary" className={styles.message} sx={{ mb: 3 }}>
                      {message}
                    </Typography>
                    <Box className={styles.redirectBox}>
                      <Typography variant="body1" className={styles.redirectText}>
                        <span className={styles.bold}>Redirecting to login page in {countdown} seconds</span>
                      </Typography>
                      <CircularProgress 
                        size={24} 
                        sx={{ mt: 2, color: '#1565c0' }}
                      />
                    </Box>
                    <Button
                      variant="contained"
                      className={styles.successButton}
                      onClick={() => navigate(`/sign-in/${userType}`)}
                      fullWidth
                      sx={{ mt: 3 }}
                    >
                      Go to Login Now
                    </Button>
                  </Box>
                )}

                {/* Error State */}
                {status === 'error' && (
                  <Box className={styles.centerContent}>
                    <Box className={styles.errorIconContainer}>
                      <Error className={styles.errorIcon} />
                    </Box>
                    <Typography variant="h4" className={styles.errorTitle} sx={{ mt: 2, mb: 2 }}>
                      Verification Failed
                    </Typography>
                    <Box className={styles.errorBox}>
                      <Typography variant="body1" className={styles.errorMessage}>
                        {message}
                      </Typography>
                    </Box>
                    
                    {/* Show appropriate action button based on error code */}
                    {(errorCode === 'EXPIRED' || errorCode === 'INVALID_TOKEN') && (
                      <Button
                        variant="contained"
                        className={styles.actionButton}
                        onClick={() => navigate('/advisor-registration')}
                        fullWidth
                        sx={{ mt: 3 }}
                      >
                        Request New Verification Link
                      </Button>
                    )}
                    
                    {errorCode === 'ALREADY_USED' && (
                      <Button
                        variant="contained"
                        className={styles.successButton}
                        onClick={() => navigate(`/sign-in/${userType}`)}
                        fullWidth
                        sx={{ mt: 3 }}
                      >
                        Go to Login
                      </Button>
                    )}
                    
                    {!errorCode && (
                      <Button
                        variant="contained"
                        className={styles.actionButton}
                        onClick={() => navigate('/advisor-registration')}
                        fullWidth
                        sx={{ mt: 3 }}
                      >
                        Try Again
                      </Button>
                    )}
                  </Box>
                )}
                
              </CardContent>
            </Card>
          </Grow>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VerifyEmail;
